import { useState, useEffect } from 'react';
import './SearchBar.css';
import { Link } from 'react-router-dom';
import { formatDateUS } from '../../utils/helper';
import { fetchSuggestions } from '../../api/suggestions';
import { getSuggestionDetails } from '../../api/getSuggestionDetails';
import { Analytics } from '../../api/analytics';

function SearchBar() {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [companyDetails, setCompanyDetails] = useState({});
  const [detailLoadingIndex, setDetailLoadingIndex] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalEstimate, setTotalEstimate] = useState(0);

  const triggerSearch = () => {
    const cleanedQuery = searchTerm.trim();
    if (!cleanedQuery) {
      setSuggestions([]);
      setTotalEstimate(0);
      return;
    }

    setExpandedIndex(null);
    setIsLoading(true);

    const payload = {
      query: cleanedQuery,
      page,
      page_size: pageSize
    };

    fetchSuggestions(payload)
      .then((data) => {
        setSuggestions(data.result || []);
        setTotalEstimate(data.total_estimate || 0);
      })
      .catch((error) => {
        console.error("Error fetching suggestions:", error);
        setSuggestions([]);
        setTotalEstimate(0);
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      triggerSearch();
    }, 500);

    return () => {
      clearTimeout(handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, page]);

  const handleInputChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1); // reset to page 1 on new term
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    triggerSearch();
  };

  const toggleExpand = async (index) => {
    const selected = suggestions[index];
    const glueId = selected?.glue_id;

    if (!glueId) return;

    if (expandedIndex === index) {
      setExpandedIndex(null);
    } else {
      setExpandedIndex(index);

      if (!companyDetails[glueId]) {
        try {
          setDetailLoadingIndex(index);
          const details = await getSuggestionDetails(glueId);
          if (details) {
            setCompanyDetails((prev) => ({ ...prev, [glueId]: details }));
          }
        } catch (error) {
          console.error("Error fetching company details:", error);
        } finally {
          setDetailLoadingIndex(null);
        }
      }
    }
  };

  const totalPages = Math.ceil(totalEstimate / pageSize);

  return (
    <div className="search-bar-wrapper">
      <form className="search-bar-container" onSubmit={handleSubmit}>
        <input
          type="text"
          className="search-input"
          placeholder="Search"
          value={searchTerm}
          onChange={handleInputChange}
          aria-label="Search Company"
        />
        <div className="search-options">
          <button type="submit" className="search-button" aria-label="Search Button">
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 
                       16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 
                       16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 
                       4.99L20.49 19l-4.99-5zm-6 
                       0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 
                       14 7.01 14 9.5 11.99 14 9.5 14z" />
            </svg>
          </button>
        </div>
      </form>

      {searchTerm && (
        <ul className="suggestions-dropdown">
          {isLoading ? (
            <li className="loading">Loading...</li>
          ) : suggestions.length === 0 ? (
            <li className="no-suggestions">No results found.</li>
          ) : (
            <>
              {suggestions.map((suggestion, index) => {
                const glueId = suggestion.glue_id;
                const details = companyDetails[glueId];
                const isLoadingDetails = detailLoadingIndex === index;

                return (
                  <li key={index} className="suggestion-item">
                    <div
                      className="suggestion-main"
                      onClick={() => toggleExpand(index)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && toggleExpand(index)}
                    >
                      <strong>{suggestion.company_name}</strong>
                      <div className="expand-icon" />
                    </div>

                    {expandedIndex === index && (
                      <div className="suggestion-details">
                        {isLoadingDetails ? (
                          <p>Loading details...</p>
                        ) : details ? (
                          <>
                            <p><strong>Glue Id:</strong> {glueId || "-"}</p>
                            <p><strong>Status:</strong> {details.status || "-"}</p>
                            <p><strong>Jurisdiction:</strong> {details.jurisdiction || "-"}</p>
                            <p><strong>Incorporation Date:</strong> {formatDateUS(details.incorporation_date) || "-"}</p>
                            <p><strong>Date Captured:</strong> {formatDateUS(details.date_captured) || "-"}</p>
                            <Link
                              className="link-style"
                              to={`/summary/${encodeURIComponent(glueId)}`}
                              // onClick={() => Analytics.logEvent('landing_page_view_summary', { glueId })}
                            >
                              View Summary →
                            </Link>
                          </>
                        ) : (
                          <p>Error loading company details.</p>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
              {totalPages > 1 ?
                <li className="pagination-controls">
                  <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                    Prev
                  </button>
                  <span>Page {page} of {totalPages}</span>
                  <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                    Next
                  </button>
                </li>
                :
                <li className="pagination-controls">
                  <span>Page {page} of {page}</span>
                </li>
              }
            </>
          )}
        </ul>
      )}
    </div>
  );
}

export default SearchBar;
